import { Component, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ApiService } from './api.service';

@Component({
  selector: 'app-admin-project-pdf-viewer',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <div class="pdf-workspace">
      <header class="workspace-header">
        <div class="min-w-0">
          <a [routerLink]="backLink()" class="back-link">
            <i class="ti ti-arrow-left"></i> {{ backLabel() }}
          </a>
          <h1>{{ title() }}</h1>
          <p>Full-page document preview.</p>
        </div>
        <div class="status-pill">
          <i class="ti ti-file-type-pdf"></i>
          PDF
        </div>
      </header>

      @if (isLoading()) {
        <div class="state">
          <i class="ti ti-loader animate-spin"></i>
          <span>Loading document...</span>
        </div>
      } @else if (error()) {
        <div class="state error">{{ error() }}</div>
      } @else if (safeUrl()) {
        <main class="viewer-shell">
          <iframe [src]="safeUrl()!" title="PDF document"></iframe>
        </main>
      }
    </div>
  `,
  styles: [`
    .pdf-workspace { position: fixed; inset: 0; z-index: 80; display: flex; flex-direction: column; background: #f4f8fb; color: #0f172a; }
    .workspace-header { min-height: 72px; display: flex; justify-content: space-between; align-items: center; gap: 16px; padding: 12px 18px; background: #fff; border-bottom: 1px solid #dbe7ef; box-shadow: 0 8px 24px rgba(15, 23, 42, .06); }
    h1 { margin: 3px 0 0; color: #0b344d; font-size: 18px; font-weight: 700; letter-spacing: -.01em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    p { margin: 2px 0 0; color: #64748b; font-size: 12px; }
    .back-link { color: #64748b; font-size: 12px; font-weight: 700; text-decoration: none; display: inline-flex; align-items: center; gap: 5px; cursor: pointer; }
    .back-link:hover { color: #1A6FA6; }
    .status-pill { height: 34px; padding: 0 12px; border: 1px solid #d5ebf6; border-radius: 8px; background: #eaf5fa; color: #1a6fa6; font-size: 12px; font-weight: 800; display: inline-flex; align-items: center; gap: 7px; }
    .viewer-shell { flex: 1; min-height: 0; padding: 8px; display: flex; }
    iframe { flex: 1; width: 100%; border: 1px solid #dbe7ef; border-radius: 8px; background: #fff; box-shadow: 0 12px 32px rgba(15, 23, 42, .08); }
    .state { margin: 16px; padding: 28px; border: 1px solid #dbe7ef; border-radius: 8px; background: #fff; color: #64748b; text-align: center; display: flex; align-items: center; justify-content: center; gap: 10px; font-weight: 700; }
    .error { color: #be123c; background: #fff1f2; border-color: #fecdd3; }
  `]
})
export class AdminProjectPdfViewerComponent implements OnDestroy {
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  private api = inject(ApiService);
  private sanitizer = inject(DomSanitizer);

  isLoading = signal(true);
  error = signal('');
  title = signal('Project Document');
  blobUrl = signal('');
  safeUrl = signal<SafeResourceUrl | null>(null);
  backLink = signal<any[] | string>('/admin/projects');
  backLabel = signal('Back to Projects');

  ngOnInit(): void {
    const type = this.route.snapshot.paramMap.get('docType') || this.route.snapshot.data['docType'] || '';
    const id = this.route.snapshot.paramMap.get('id') || '';
    const projectId = this.route.snapshot.queryParamMap.get('projectId') || '';
    if (this.route.snapshot.data['publicBack']) {
      this.backLink.set(['/declaration', id]);
      this.backLabel.set('Back to Declaration');
    } else if (projectId) {
      this.backLink.set(['/admin/projects', projectId]);
      this.backLabel.set('Back to Project');
    }
    const endpoint = this.endpointFor(type, id);
    if (!endpoint) {
      this.isLoading.set(false);
      this.error.set('Unknown project document type.');
      return;
    }

    this.title.set(endpoint.title);
    if (endpoint.backLink) {
      this.backLink.set(endpoint.backLink);
      this.backLabel.set(endpoint.backLabel || 'Back');
    }

    if (endpoint.method === 'POST') {
      this.http.post(endpoint.url, endpoint.body || {}, { headers: this.requestHeaders() }).subscribe({
        next: (res: any) => {
          this.isLoading.set(false);
          if (res.success && res.data?.pdf_base64) {
            this.openBase64Pdf(res.data.pdf_base64);
            return;
          }
          this.error.set(res.message || 'Could not load document.');
        },
        error: () => {
          this.isLoading.set(false);
          this.error.set('Could not load document.');
        }
      });
      return;
    }

    this.http.get(endpoint.url, { headers: this.requestHeaders(), responseType: 'blob' }).subscribe({
      next: (blob) => {
        this.isLoading.set(false);
        if (!this.isPdfBlob(blob)) {
          this.readErrorBlob(blob);
          return;
        }
        const url = URL.createObjectURL(blob);
        this.blobUrl.set(url);
        this.safeUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(url));
      },
      error: () => {
        this.isLoading.set(false);
        this.error.set('Could not load document.');
      }
    });
  }

  ngOnDestroy(): void {
    const url = this.blobUrl();
    if (url) {
      URL.revokeObjectURL(url);
    }
  }

  private endpointFor(type: string, id: string): {
    title: string;
    url: string;
    method: 'GET' | 'POST';
    body?: Record<string, unknown>;
    backLink?: any[];
    backLabel?: string;
  } | null {
    if (!id) return null;

    if (type === 'quote') {
      return {
        title: 'Quote PDF',
        url: `${this.api.baseUrl}/quotes/pdf.php?id=${encodeURIComponent(id)}`,
        method: 'GET',
        backLink: ['/admin/financials/quotes', id],
        backLabel: 'Back to Quote'
      };
    }

    if (type === 'purchase-order') {
      return {
        title: 'Purchase Order PDF',
        url: `${this.api.baseUrl}/purchase_orders/pdf.php?id=${encodeURIComponent(id)}`,
        method: 'GET',
        backLink: ['/admin/financials/purchase-orders', id],
        backLabel: 'Back to Purchase Order'
      };
    }

    if (type === 'work-order') {
      return {
        title: 'Work Order PDF',
        url: `${this.api.baseUrl}/work_orders/pdf.php?id=${encodeURIComponent(id)}`,
        method: 'GET',
        backLink: ['/admin/financials/work-orders', id],
        backLabel: 'Back to Work Order'
      };
    }

    if (type === 'invoice') {
      return {
        title: 'Invoice PDF',
        url: `${this.api.baseUrl}/invoices/generate_pdf.php`,
        method: 'POST',
        body: { invoice_id: id },
        backLink: ['/admin/financials/invoices', id],
        backLabel: 'Back to Invoice'
      };
    }

    const token = encodeURIComponent(this.api.accessToken());
    const base = `${this.api.baseUrl}/projects`;
    const map: Record<string, { title: string; script: string; param: string }> = {
      'first-fix': { title: 'First Fix Document', script: 'generate_first_fix_pdf.php', param: 'phase_id' },
      'second-fix': { title: 'Second Fix Document', script: 'generate_second_fix_pdf.php', param: 'phase_id' },
      'declaration': { title: 'Customer Declaration', script: 'declaration_pdf.php', param: 'id' },
      'ps3-plumbing': { title: 'PS3 Plumbing Certificate', script: 'generate_ps3_plumbing.php', param: 'id' },
      'ps3-drainage': { title: 'PS3 Drainage Certificate', script: 'generate_ps3_drainage.php', param: 'id' },
      'gas-certificate': { title: 'Gas Safety Certificate', script: 'generate_gas_certificate.php', param: 'id' }
    };
    const doc = map[type];
    if (!doc) return null;
    return {
      title: doc.title,
      method: 'GET',
      url: `${base}/${doc.script}?${doc.param}=${encodeURIComponent(id)}&token=${token}`
    };
  }

  private openBase64Pdf(pdfBase64: string): void {
    const byteCharacters = atob(pdfBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    this.blobUrl.set(url);
    this.safeUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(url));
  }

  private requestHeaders(): HttpHeaders {
    const token = this.api.accessToken();
    return new HttpHeaders({
      'Authorization': token ? `Bearer ${token}` : ''
    });
  }

  private isPdfBlob(blob: Blob): boolean {
    return !blob.type || blob.type === 'application/pdf' || blob.type === 'application/octet-stream';
  }

  private readErrorBlob(blob: Blob): void {
    const reader = new FileReader();
    reader.onload = () => {
      const message = String(reader.result || '').trim();
      this.error.set(message || 'Could not load document.');
    };
    reader.onerror = () => this.error.set('Could not load document.');
    reader.readAsText(blob);
  }
}
